import React, { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { toast } from "sonner";
import { TOAST_DEFAULT_DURATION_MS } from "@/shared/config/api";
import { AppToaster } from "@/shared/ui/toast/AppToaster";
import { ToastCard, type ToastReplyConfig, type ToastType } from "@/shared/ui/toast/ToastCard";

const LONG_DURATION_MS = 8000;

interface ChatToastOptions {
    onClick?: () => void;
    onReply?: (text: string) => Promise<void> | void;
    replyPlaceholder?: string;
    avatarUrl?: string;
}

interface ToastContextType {
    showToast: (type: ToastType, title: string, message: string, duration?: number, onClick?: () => void, reply?: ToastReplyConfig, avatarUrl?: string) => void;
    success: (message: string, title?: string, onClick?: () => void) => void;
    error: (message: string, title?: string, onClick?: () => void) => void;
    warning: (message: string, title?: string, onClick?: () => void) => void;
    info: (message: string, title?: string, onClick?: () => void) => void;
    system: (message: string, title?: string, onClick?: () => void) => void;
    chatMessage: (message: string, title: string, options?: ChatToastOptions) => void;
}

interface ToastContent {
    type: ToastType;
    title: string;
    message: string;
    onClick?: () => void;
    reply?: ToastReplyConfig;
    avatarUrl?: string;
}

function openToast(content: ToastContent, duration: number) {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const render = (onReplyOpen?: () => void) =>
        function ToastRender() {
            return <ToastCard {...content} onDismiss={() => toast.dismiss(id)} onReplyOpen={onReplyOpen} />;
        };
    // Reemitir com o mesmo id congela o timer: fechar por baixo de quem digita perde o texto.
    const freeze = () => {
        toast.custom(render(), { id, duration: Infinity });
    };
    toast.custom(render(freeze), { id, duration });
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};

// Para telas que tambem renderizam fora de um ToastProvider: sem provider, devolve null em vez de lancar.
export const useOptionalToast = (): ToastContextType | null => useContext(ToastContext) ?? null;

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const showToast = useCallback(
        (type: ToastType, title: string, message: string, duration: number = TOAST_DEFAULT_DURATION_MS, onClick?: () => void, reply?: ToastReplyConfig, avatarUrl?: string) => {
            openToast({ type, title, message, onClick, reply, avatarUrl }, duration);
        },
        []
    );

    const success = useCallback(
        (message: string, title: string = "Concluído", onClick?: () => void) => showToast("success", title, message, TOAST_DEFAULT_DURATION_MS, onClick),
        [showToast]
    );

    const error = useCallback(
        (message: string, title: string = "Erro", onClick?: () => void) => showToast("error", title, message, TOAST_DEFAULT_DURATION_MS, onClick),
        [showToast]
    );

    const warning = useCallback(
        (message: string, title: string = "Atenção", onClick?: () => void) => showToast("warning", title, message, TOAST_DEFAULT_DURATION_MS, onClick),
        [showToast]
    );

    const info = useCallback(
        (message: string, title: string = "Informativo", onClick?: () => void) => showToast("info", title, message, TOAST_DEFAULT_DURATION_MS, onClick),
        [showToast]
    );

    const system = useCallback(
        (message: string, title: string = "Sistema", onClick?: () => void) => showToast("info", title, message, LONG_DURATION_MS, onClick),
        [showToast]
    );

    const chatMessage = useCallback(
        (message: string, title: string = "Nova mensagem", options?: ChatToastOptions) => {
            const reply: ToastReplyConfig | undefined = options?.onReply
                ? { onSend: options.onReply, placeholder: options.replyPlaceholder }
                : undefined;
            showToast("info", title, message, LONG_DURATION_MS, options?.onClick, reply, options?.avatarUrl);
        },
        [showToast]
    );

    const contextValue = useMemo(
        () => ({ showToast, success, error, warning, info, system, chatMessage }),
        [showToast, success, error, warning, info, system, chatMessage]
    );

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <AppToaster />
        </ToastContext.Provider>
    );
};
